import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import DynamicForm from "../../components/form/DynamicForm";
import { getBuildingInputs } from "../../utils/building.inputs";
import {
  createNewBuilding,
  getBuildingDetailsById,
  updateBuildingDetails,
} from "../../services/api/building";
import { validateBuildingFields } from "../../utils/validation/validation";
import {
  CREATE_UPDATE_BUTTON,
  RESET_CANCEL_BUTTON,
} from "../../styles/ButtonStyles";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/slices/toastSlice";

export default function CreateUpdateBuilding() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams();
  const isEditMode = Boolean(id);
  const formInputs = getBuildingInputs();

  const initialFormState = {
    building_name: "",
    building_address: "",
  };
  const [buildingDetails, setBuildingDetails] = useState(initialFormState);

  useEffect(() => {
    const fetchBuildingDetails = async () => {
      if (isEditMode) {
        try {
          const response = await getBuildingDetailsById(id);
          if (response.responseCode === 200) {
            setBuildingDetails(response.data);
          }
        } catch (error) {
          dispatch(showToast({ message: error.message, severity: "error" }));
        }
      }
    };

    fetchBuildingDetails();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBuildingDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationResult = validateBuildingFields(buildingDetails);
    if (!validationResult.valid) {
      dispatch(
        showToast({ message: validationResult.message, severity: "error" })
      );
      return;
    }

    try {
      let response;
      if (isEditMode) {
        response = await updateBuildingDetails(id, buildingDetails);
      } else {
        response = await createNewBuilding(buildingDetails);
      }

      if (response.responseCode === 200 || response.responseCode === 201) {
        dispatch(showToast({ message: response.message, severity: "success" }));
        localStorage.setItem(
          isEditMode ? "editBuildingSuccess" : "addNewBuildingSuccess",
          response.message
        );
        navigate("/building-list");
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  const handleCancelOrReset = () => {
    if (isEditMode) {
      // Navigate back if editing
      navigate("/building-list");
    } else {
      // Reset form if adding
      setBuildingDetails(initialFormState);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 4,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          backgroundColor: "#fff",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ mb: 2, color: "#333" }}
        >
          {isEditMode ? "Update Building" : "Add New Building"}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 1, mb: 1 }}
        >
          {isEditMode
            ? "Modify the building information below and click update when done."
            : "Fill out the building details to add a new record to the system."}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleSubmit}>
          <DynamicForm
            inputs={formInputs}
            values={buildingDetails}
            onChange={handleChange}
          />

          <Divider />

          <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
            <Button type="submit" variant="contained" sx={CREATE_UPDATE_BUTTON}>
              {isEditMode ? "Update Building" : "Create Building"}
            </Button>

            <Button
              type="button"
              variant="text"
              onClick={handleCancelOrReset}
              sx={RESET_CANCEL_BUTTON}
            >
              {isEditMode ? "Cancel" : "Reset"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
